import { supabase } from '../lib/supabase';
import { formatDate } from '../utils/formatters';

/**
 * Admin Service
 * Handles all platform administration operations
 * Requires admin role for all operations
 */

export const adminService = {
  /**
   * Get platform statistics
   */
  async getPlatformStats() {
    try {
      const [usersCount, conferencesCount, submissionsCount, reviewsCount] = await Promise.all([
        supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true }),
        supabase?.from('conferences')?.select('id', { count: 'exact', head: true }),
        supabase?.from('conference_submissions')?.select('id', { count: 'exact', head: true }),
        supabase?.from('submission_reviews')?.select('id', { count: 'exact', head: true })
      ]);

      return {
        totalUsers: usersCount?.count || 0,
        totalConferences: conferencesCount?.count || 0,
        totalSubmissions: submissionsCount?.count || 0,
        totalReviews: reviewsCount?.count || 0
      };
    } catch (error) {
      console.error('Get platform stats error:', error);
      throw error;
    }
  },

  /**
   * Get all users with filters
   */
  async getAllUsers(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select('id, email, first_name, last_name, institution, roles, is_active, created_at')?.order('created_at', { ascending: false });

      if (filters?.roles && filters?.roles?.length > 0) {
        query = query?.contains('roles', filters?.roles);
      }

      if (filters?.search) {
        query = query?.or(`first_name.ilike.%${filters?.search}%,last_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
      }

      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(user => ({
        id: user?.id,
        email: user?.email,
        firstName: user?.first_name,
        lastName: user?.last_name,
        institution: user?.institution,
        roles: user?.roles,
        // Platform admin is its own column now; fall back to the legacy
        // array so this reads correctly before and after the migration.
        isPlatformAdmin: user?.is_platform_admin ?? user?.roles?.includes('admin') ?? false,
        isActive: user?.is_active,
        createdAt: user?.created_at
      })) || [];
    } catch (error) {
      console.error('Get all users error:', error);
      throw error;
    }
  },

  /**
   * Update user status
   */
  async updateUserStatus(userId, isActive) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ is_active: isActive })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update user status error:', error);
      throw error;
    }
  },

  /**
   * Update user roles
   */
  async updateUserRoles(userId, roles) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ roles: roles })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update user roles error:', error);
      throw error;
    }
  },

  /**
   * Delete user
   */
  async deleteUser(userId) {
    try {
      const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  },

  /**
   * Get all conferences with details
   */
  async getAllConferences(filters = {}) {
    try {
      let query = supabase?.from('conferences')?.select(`
          *,
          user_profiles!conferences_created_by_fkey(first_name, last_name, email)
        `)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.search) {
        query = query?.or(`name.ilike.%${filters?.search}%,short_name.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(conf => ({
        id: conf?.id,
        name: conf?.name,
        shortName: conf?.short_name,
        status: conf?.status,
        startDate: conf?.start_date,
        endDate: conf?.end_date,
        paperDeadline: conf?.paper_deadline,
        organizerName: `${conf?.user_profiles?.first_name} ${conf?.user_profiles?.last_name}`,
        organizerEmail: conf?.user_profiles?.email,
        createdAt: conf?.created_at
      })) || [];
    } catch (error) {
      console.error('Get all conferences error:', error);
      throw error;
    }
  },

  /**
   * Update conference status
   */
  async updateConferenceStatus(conferenceId, status) {
    try {
      const { data, error } = await supabase?.from('conferences')?.update({ status: status })?.eq('id', conferenceId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update conference status error:', error);
      throw error;
    }
  },

  /**
   * Delete conference
   */
  async deleteConference(conferenceId) {
    try {
      const { error } = await supabase?.from('conferences')?.delete()?.eq('id', conferenceId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete conference error:', error);
      throw error;
    }
  },

  /**
   * Get all submissions with details
   */
  async getAllSubmissions(filters = {}) {
    try {
      let query = supabase?.from('conference_submissions')?.select(`
          *,
          conferences(name, short_name),
          user_profiles(first_name, last_name, email)
        `)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.conferenceId) {
        query = query?.eq('conference_id', filters?.conferenceId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(sub => ({
        id: sub?.id,
        title: sub?.title,
        status: sub?.status,
        conferenceName: sub?.conferences?.name,
        authorName: `${sub?.user_profiles?.first_name} ${sub?.user_profiles?.last_name}`,
        authorEmail: sub?.user_profiles?.email,
        submittedAt: sub?.submitted_at,
        createdAt: sub?.created_at
      })) || [];
    } catch (error) {
      console.error('Get all submissions error:', error);
      throw error;
    }
  },

  /**
   * Get analytics data for the admin dashboard charts
   */
  async getAnalyticsData() {
    try {
      const [usersRes, submissionsRes, conferencesRes, reviewsRes] = await Promise.all([
        supabase?.from('user_profiles')?.select('id, created_at'),
        supabase?.from('conference_submissions')?.select('id, status, created_at, conference_id, conferences(name, short_name)'),
        supabase?.from('conferences')?.select('id, name, short_name, status, created_at'),
        supabase?.from('submission_reviews')?.select('id, created_at, overall_recommendation')
      ]);

      const users = usersRes?.data || [];
      const submissions = submissionsRes?.data || [];
      const conferences = conferencesRes?.data || [];
      const reviews = reviewsRes?.data || [];

      const now = new Date();
      const months = [];
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({
          key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
          label: formatDate(d, { month: 'short', year: '2-digit', day: undefined }),
          users: 0,
          submissions: 0,
          reviews: 0,
          conferences: 0
        });
      }

      const toMonthKey = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      };

      users.forEach(u => {
        const key = toMonthKey(u?.created_at);
        const month = months.find(m => m.key === key);
        if (month) month.users++;
      });
      submissions.forEach(s => {
        const key = toMonthKey(s?.created_at);
        const month = months.find(m => m.key === key);
        if (month) month.submissions++;
      });
      reviews.forEach(r => {
        const key = toMonthKey(r?.created_at);
        const month = months.find(m => m.key === key);
        if (month) month.reviews++;
      });
      conferences.forEach(c => {
        const key = toMonthKey(c?.created_at);
        const month = months.find(m => m.key === key);
        if (month) month.conferences++;
      });

      // Cumulative user growth
      let cumulative = users.length - months.reduce((sum, m) => sum + m.users, 0);
      months.forEach(m => {
        cumulative += m.users;
        m.cumulativeUsers = Math.max(0, cumulative);
      });

      // Status distributions
      const countBy = (arr, field) => {
        const counts = {};
        arr.forEach(item => {
          const val = item?.[field] || 'unknown';
          counts[val] = (counts[val] || 0) + 1;
        });
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
      };

      const submissionStatusData = countBy(submissions, 'status');
      const conferenceStatusData = countBy(conferences, 'status');
      const reviewDecisionData = countBy(reviews, 'overall_recommendation');

      // Top conferences by submissions
      const confCounts = {};
      submissions.forEach(s => {
        const name = s?.conferences?.short_name || s?.conferences?.name || 'Unknown';
        confCounts[name] = (confCounts[name] || 0) + 1;
      });
      const topConferences = Object.entries(confCounts)
        .map(([name, submissions]) => ({ name, submissions }))
        .sort((a, b) => b.submissions - a.submissions)
        .slice(0, 8);

      return {
        monthlyTrends: months,
        submissionStatusData,
        conferenceStatusData,
        reviewDecisionData,
        topConferences,
        totals: { users: users.length, submissions: submissions.length, conferences: conferences.length, reviews: reviews.length }
      };
    } catch (error) {
      console.error('Get analytics data error:', error);
      throw error;
    }
  },

  /**
   * Get activity logs (recent changes)
   */
  async getActivityLogs(limit = 50) {
    try {
      const [recentUsers, recentConferences, recentSubmissions] = await Promise.all([
        supabase?.from('user_profiles')?.select('id, first_name, last_name, email, created_at')?.order('created_at', { ascending: false })?.limit(10),
        supabase?.from('conferences')?.select('id, name, status, created_at')?.order('created_at', { ascending: false })?.limit(10),
        supabase?.from('conference_submissions')?.select('id, title, status, created_at')?.order('created_at', { ascending: false })?.limit(10)
      ]);

      const activities = [];

      recentUsers?.data?.forEach(user => {
        activities?.push({
          type: 'user',
          action: 'registered',
          description: `${user?.first_name} ${user?.last_name} registered`,
          timestamp: user?.created_at
        });
      });

      recentConferences?.data?.forEach(conf => {
        activities?.push({
          type: 'conference',
          action: 'created',
          description: `Conference "${conf?.name}" created`,
          timestamp: conf?.created_at
        });
      });

      recentSubmissions?.data?.forEach(sub => {
        activities?.push({
          type: 'submission',
          action: 'submitted',
          description: `Paper "${sub?.title}" submitted`,
          timestamp: sub?.created_at
        });
      });

      return activities?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))?.slice(0, limit);
    } catch (error) {
      console.error('Get activity logs error:', error);
      throw error;
    }
  },

  /**
   * Get user login logs with user details
   */
  async getLoginLogs(limit = 100) {
    try {
      const { data, error } = await supabase
        ?.from('user_login_logs')
        ?.select('*, user_profiles(first_name, last_name, email)')
        ?.order('logged_in_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        userId: log?.user_id,
        userName: `${log?.user_profiles?.first_name || ''} ${log?.user_profiles?.last_name || ''}`.trim() || 'Unknown',
        userEmail: log?.user_profiles?.email || '',
        ipAddress: log?.ip_address,
        country: log?.country,
        countryCode: log?.country_code,
        city: log?.city,
        region: log?.region,
        latitude: log?.latitude,
        longitude: log?.longitude,
        timezone: log?.timezone,
        browser: log?.browser,
        os: log?.os,
        deviceType: log?.device_type,
        loggedInAt: log?.logged_in_at,
      })) || [];
    } catch (error) {
      console.error('Get login logs error:', error);
      throw error;
    }
  },

  /**
   * Get aggregated user insights (countries, browsers, etc.)
   */
  async getUserInsights() {
    try {
      const { data, error } = await supabase
        ?.from('user_login_logs')
        ?.select('country, country_code, city, browser, os, device_type, logged_in_at');

      if (error) throw error;

      const logs = data || [];

      // Country distribution
      const countryCounts = {};
      logs.forEach(l => {
        const c = l?.country || 'Unknown';
        countryCounts[c] = (countryCounts[c] || 0) + 1;
      });
      const countryData = Object.entries(countryCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

      // Browser distribution
      const browserCounts = {};
      logs.forEach(l => {
        const b = l?.browser || 'Unknown';
        browserCounts[b] = (browserCounts[b] || 0) + 1;
      });
      const browserData = Object.entries(browserCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

      // OS distribution
      const osCounts = {};
      logs.forEach(l => {
        const o = l?.os || 'Unknown';
        osCounts[o] = (osCounts[o] || 0) + 1;
      });
      const osData = Object.entries(osCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

      // Device type distribution
      const deviceCounts = {};
      logs.forEach(l => {
        const d = l?.device_type || 'Unknown';
        deviceCounts[d] = (deviceCounts[d] || 0) + 1;
      });
      const deviceData = Object.entries(deviceCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

      // Logins per day (last 30 days)
      const now = new Date();
      const days = [];
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const key = d.toISOString().split('T')[0];
        days.push({ date: key, label: formatDate(d, { month: 'short', day: 'numeric', year: undefined }), logins: 0 });
      }
      logs.forEach(l => {
        if (!l?.logged_in_at) return;
        const key = new Date(l.logged_in_at).toISOString().split('T')[0];
        const day = days.find(d => d.date === key);
        if (day) day.logins++;
      });

      // Top cities
      const cityCounts = {};
      logs.forEach(l => {
        if (!l?.city) return;
        const label = l?.country ? `${l.city}, ${l.country}` : l.city;
        cityCounts[label] = (cityCounts[label] || 0) + 1;
      });
      const topCities = Object.entries(cityCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      return {
        totalLogins: logs.length,
        countryData,
        browserData,
        osData,
        deviceData,
        dailyLogins: days,
        topCities,
        uniqueCountries: countryData.length,
        uniqueCities: Object.keys(cityCounts).length,
      };
    } catch (error) {
      console.error('Get user insights error:', error);
      throw error;
    }
  }
};